export interface PanelSize {
  width: number
  height: number
}

export const DEFAULT_SIZE: PanelSize = { width: 400, height: 540 }
export const LARGE_SIZE: PanelSize = { width: 640, height: 820 }
export const MIN_SIZE: PanelSize = { width: 360, height: 420 }

const STORAGE_KEY = 'morta.chat.panelSize'
const MARGIN_X = 48
const MARGIN_Y = 120

interface Viewport {
  vw: number
  vh: number
}

const currentViewport = (): Viewport => ({
  vw: typeof window === 'undefined' ? 1280 : window.innerWidth,
  vh: typeof window === 'undefined' ? 800 : window.innerHeight
})

export const clampSize = (size: PanelSize, viewport: Viewport = currentViewport()): PanelSize => {
  // Upper bound: viewport minus margin (floor of 1 to avoid zero/negative dimensions).
  // Lower bound: MIN_SIZE, unless the viewport itself is smaller — then fitting on
  // screen wins over the configured minimum, so the panel never overflows.
  const maxW = Math.max(1, viewport.vw - MARGIN_X)
  const maxH = Math.max(1, viewport.vh - MARGIN_Y)
  const minW = Math.min(MIN_SIZE.width, maxW)
  const minH = Math.min(MIN_SIZE.height, maxH)
  return {
    width: Math.min(Math.max(size.width, minW), maxW),
    height: Math.min(Math.max(size.height, minH), maxH)
  }
}

export const readPanelSize = (): PanelSize => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_SIZE
    const parsed = JSON.parse(raw)
    // Number.isFinite rejects NaN/Infinity from corrupted or hand-edited storage,
    // which would otherwise propagate through clampSize into invalid style values.
    if (!Number.isFinite(parsed?.width) || !Number.isFinite(parsed?.height)) return DEFAULT_SIZE
    return { width: parsed.width, height: parsed.height }
  } catch {
    return DEFAULT_SIZE
  }
}

export const writePanelSize = (size: PanelSize): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(size))
  } catch {
    // localStorage unavailable (private mode / quota) — non-fatal.
  }
}

export const nextPresetSize = (current: PanelSize, viewport: Viewport = currentViewport()): PanelSize => {
  const large = clampSize(LARGE_SIZE, viewport)
  const distToDefault = Math.hypot(current.width - DEFAULT_SIZE.width, current.height - DEFAULT_SIZE.height)
  const distToLarge = Math.hypot(current.width - large.width, current.height - large.height)
  // Clamp the default preset too, so toggling back never overflows a small viewport.
  return distToDefault <= distToLarge ? large : clampSize(DEFAULT_SIZE, viewport)
}
